import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  endOfYear,
  subYears,
} from 'date-fns';
import { CalendarIcon } from 'lucide-react';

import { getDateFnsLocale } from '@/lib/utils/date';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';

export interface TeamDateRange {
  from?: Date;
  to?: Date;
}

// Same quick ranges as the KPI page (week starts Monday to match the work week).
const RANGE_PRESETS: {
  labelKey: string;
  range: (now: Date) => { from: Date; to: Date };
}[] = [
  {
    labelKey: 'time.thisWeek',
    range: (now) => ({
      from: startOfWeek(now, { weekStartsOn: 1 }),
      to: endOfWeek(now, { weekStartsOn: 1 }),
    }),
  },
  {
    labelKey: 'time.thisMonth',
    range: (now) => ({ from: startOfMonth(now), to: endOfMonth(now) }),
  },
  {
    labelKey: 'time.lastMonth',
    range: (now) => {
      const d = subMonths(now, 1);
      return { from: startOfMonth(d), to: endOfMonth(d) };
    },
  },
  {
    labelKey: 'time.thisYear',
    range: (now) => ({ from: startOfYear(now), to: endOfYear(now) }),
  },
  {
    labelKey: 'time.lastYear',
    range: (now) => {
      const d = subYears(now, 1);
      return { from: startOfYear(d), to: endOfYear(d) };
    },
  },
];

interface TeamRangePickerProps {
  value: TeamDateRange;
  onChange: (range: TeamDateRange) => void;
}

/** The KPI-page date-range picker, reused for the Team Efficiency block. */
export function TeamRangePicker({ value, onChange }: TeamRangePickerProps) {
  const { t, i18n } = useTranslation();
  const dateFnsLocale = getDateFnsLocale(i18n.language);

  // Cap current-period presets at today so "this month/year" stays to-date.
  const applyPreset = (preset: (typeof RANGE_PRESETS)[number]) => {
    const now = new Date();
    const { from, to } = preset.range(now);
    onChange({ from, to: to > now ? now : to });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="w-[230px] justify-between font-normal"
        >
          <span className="truncate">
            {value.from && value.to
              ? `${format(value.from, 'yyyy-MM-dd', { locale: dateFnsLocale })} - ${format(value.to, 'yyyy-MM-dd', { locale: dateFnsLocale })}`
              : t('createdDateRange')}
          </span>
          <div className="flex items-center shrink-0">
            <Separator orientation="vertical" className="mx-2 h-4" />
            <CalendarIcon className="h-4 w-4" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-auto p-0">
        <div className="flex flex-wrap gap-1.5 p-3 pb-0">
          {RANGE_PRESETS.map((preset) => (
            <Button
              key={preset.labelKey}
              type="button"
              variant="outline"
              size="sm"
              className="h-7 rounded-full px-3 text-xs font-normal"
              onClick={() => applyPreset(preset)}
            >
              {t(preset.labelKey)}
            </Button>
          ))}
        </div>
        <Calendar
          mode="range"
          defaultMonth={value.from}
          selected={{ from: value.from, to: value.to }}
          onSelect={(r) => onChange({ from: r?.from, to: r?.to })}
          numberOfMonths={2}
        />
      </PopoverContent>
    </Popover>
  );
}
